<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { truncateFileName } from 'shared/helpers/FileHelper';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  csvFile: {
    type: File,
    default: null,
  },
  csvFileName: {
    type: String,
    default: '',
  },
  csvHeaders: {
    type: Array,
    default: () => [],
  },
  validator: {
    type: Object,
    required: true,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
  titleError: {
    type: String,
    default: '',
  },
  validateCsvFile: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits([
  'update:title',
  'fileSelected',
  'removeFile',
  'next',
]);

const { t } = useI18n();
const fileInput = ref(null);
const isValidating = ref(false);

const handleFileClick = () => fileInput.value?.click();

const parseCSVHeaders = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const content = event.target.result;
        const firstLine = content.split('\n')[0].trim();
        const headers = firstLine
          .split(',')
          .map(header => header.trim().replace(/^"|"$/g, ''));
        resolve(headers);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const handleFileChange = async () => {
  const file = fileInput.value?.files[0];
  if (file) {
    try {
      const fileName = truncateFileName(file.name);
      const headers = await parseCSVHeaders(file);

      emit('fileSelected', { file, fileName, headers });

      useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.UPLOAD_SUCCESS'));
    } catch (error) {
      useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.PARSE_ERROR'));
    }
  }
};

const handleRemoveFile = () => {
  emit('removeFile');
  if (fileInput.value) {
    fileInput.value.value = null;
  }
};

const handleNext = async () => {
  props.validator.$touch();
  if (props.validator.$invalid) return;

  if (!props.csvFile) {
    useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.ERROR_REQUIRED'));
    return;
  }

  isValidating.value = true;
  const isValid = await props.validateCsvFile(props.csvFile);
  isValidating.value = false;

  if (isValid) {
    emit('next');
  }
};

const localTitle = computed({
  get: () => props.title,
  set: value => emit('update:title', value),
});

const nextButtonLabel = computed(() => {
  return isValidating.value
    ? t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.VALIDATING_CSV')
    : t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.NEXT');
});

const isNextButtonDisabled = computed(() => {
  return props.validator.$invalid || !props.csvFile || isValidating.value;
});
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEPS.NAME_AND_CSV') }}
      </h3>
      <div class="flex items-center gap-2">
        <div class="text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEP', {
              current: currentStep + 1,
              total: totalSteps,
            })
          }}
        </div>
      </div>
    </div>
    <Input
      v-model="localTitle"
      :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="titleError"
      :message-type="titleError ? 'error' : 'info'"
    />
    <div
      class="flex flex-col gap-2 p-4 border border-dashed rounded-md border-n-slate-6 mt-4"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.LABEL') }}
        </span>
        <div class="flex items-center gap-2">
          <span v-if="csvFile" class="text-sm text-n-slate-12">
            {{ csvFileName }}
          </span>
          <Button
            v-if="!csvFile"
            :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.CHOOSE_FILE')"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.DESCRIPTION') }}
        <a
          href="/downloads/whatsapp-campaign-audience-sample.csv"
          target="_blank"
          rel="noopener noreferrer"
          download="whatsapp-campaign-audience-sample.csv"
          class="text-n-blue-text"
        >
          {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.DOWNLOAD_SAMPLE') }}
        </a>
      </p>
      <div v-if="csvHeaders.length > 0" class="mt-2">
        <div class="text-sm font-medium text-n-slate-12 mb-1">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.CSV_FILE.DETECTED_HEADERS')
          }}:
        </div>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="header in csvHeaders"
            :key="header"
            class="px-2 py-1 text-xs bg-n-alpha-black2 rounded-md text-n-slate-12"
          >
            {{ header }}
          </span>
        </div>
      </div>
    </div>

    <div class="flex justify-end mt-4">
      <Button
        variant="solid"
        color="primary"
        type="button"
        :label="nextButtonLabel"
        :disabled="isNextButtonDisabled"
        :is-loading="isValidating"
        @click="handleNext"
      />
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
